/**
 * Dineo Design System - Kitchen Display System Theme
 *
 * High-contrast dark theme optimised for wall-mounted kitchen monitors.
 * Font sizes are 1.5x the base to ensure readability at distance.
 * Touch targets are enlarged to 64px minimum for gloved/wet hands.
 *
 * Reference: docs/ci-colors.md
 */

import { createTheme } from '@mui/material/styles';

export const kdsTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#6AAE6F',
      contrastText: '#000000',
    },
    secondary: {
      main: '#FBAE40',
      contrastText: '#000000',
    },
    background: {
      default: '#000000',
      paper: '#1A1A1A',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#CCCCCC',
    },
    divider: '#444444',
    success: {
      main: '#3D7344',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#FFAC01',
      contrastText: '#000000',
    },
    error: {
      main: '#C4362A',
      contrastText: '#FFFFFF',
    },
    orderStatus: {
      PENDING_PAYMENT: '#8B5CF6',
      PLACED: '#0989B1',
      PREPARING: '#FFAC01',
      READY: '#3D7344',
      COMPLETED: '#6B6358',
      CANCELLED: '#C4362A',
    },
    orderStatusBg: {
      PENDING_PAYMENT: '#8B5CF6',
      PLACED: '#0989B1',
      PREPARING: '#FFAC01',
      READY: '#3D7344',
      COMPLETED: '#6B6358',
      CANCELLED: '#C4362A',
    },
    orderStatusText: {
      PENDING_PAYMENT: '#FFFFFF',
      PLACED: '#FFFFFF',
      PREPARING: '#000000',
      READY: '#FFFFFF',
      COMPLETED: '#FFFFFF',
      CANCELLED: '#FFFFFF',
    },
  },

  typography: {
    htmlFontSize: 10.667,
    fontFamily: '"Raleway Variable", system-ui, sans-serif',
    h1: {
      fontSize: '3rem',
      fontWeight: 700,
    },
    h2: {
      fontSize: '2.625rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '2.25rem',
      fontWeight: 700,
    },
    h4: {
      fontSize: '1.875rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.6875rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    body1: {
      fontSize: '1.5rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '1.3125rem',
      lineHeight: 1.5,
    },
    button: {
      fontSize: '1.3125rem',
      fontWeight: 600,
      textTransform: 'none',
    },
    caption: {
      fontSize: '1.125rem',
    },
  },

  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          minHeight: 64,
          minWidth: 64,
          padding: '12px 32px',
          fontSize: '1.3125rem',
          fontWeight: 600,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minHeight: 64,
          minWidth: 64,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          overflow: 'hidden',
          backgroundColor: '#1A1A1A',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          height: 'auto',
          minHeight: 48,
          fontSize: '1.125rem',
          fontWeight: 600,
          padding: '8px 16px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          height: 72,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundColor: '#1A1A1A',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: 64,
        },
      },
    },
  },

  shape: {
    borderRadius: 12,
  },

  spacing: 8,
});
